import React, { useContext, useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledDropdown
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import "./ShopeeIssueCenter.scss";

const T = ({ id }) => <IntlMessages id={id} />;
const EMPTY_ROWS = [];

const writable = (capabilities, alias) =>
  capabilities && capabilities[alias] && capabilities[alias].write;

// Channel Badge Component
const ChannelBadge = ({ channel }) => {
  switch (channel?.toLowerCase()) {
    case "shopee":
      return (
        <div className="channel-pill">
          <span className="channel-logo shopee">S</span>
          <span>Shopee</span>
        </div>
      );
    case "tiktok shop":
    case "tiktok":
      return (
        <div className="channel-pill">
          <span className="channel-logo tiktok">TT</span>
          <span>TikTok Shop</span>
        </div>
      );
    case "lazada":
      return (
        <div className="channel-pill">
          <span className="channel-logo lazada">L</span>
          <span>Lazada</span>
        </div>
      );
    case "website":
      return (
        <div className="channel-pill">
          <span className="channel-logo website"><i className="simple-icon-globe" style={{ fontSize: 10 }} /></span>
          <span>Website</span>
        </div>
      );
    case "ghn":
      return (
        <div className="channel-pill">
          <span className="channel-logo ghn">GHN</span>
          <span>GHN</span>
        </div>
      );
    default:
      return (
        <div className="channel-pill">
          <span className="channel-logo website"><i className="simple-icon-settings" style={{ fontSize: 10 }} /></span>
          <span>{channel || <IntlMessages id="shopee.workspace.issues.channel-other" />}</span>
        </div>
      );
  }
};

// Severity Badge Component
const SeverityBadge = ({ severity }) => {
  const config = {
    critical: {
      cls: "nghiem-trong",
      id: "shopee.workspace.issues.severity.critical"
    },
    high: { cls: "cao", id: "shopee.workspace.issues.severity.high" },
    medium: {
      cls: "trung-binh",
      id: "shopee.workspace.issues.severity.medium"
    }
  }[severity] || {
    cls: "thap",
    id: "shopee.workspace.issues.severity.low"
  };
  return (
    <span className={`severity-pill ${config.cls}`}>
      <IntlMessages id={config.id} />
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status, assignee, time }) => {
  switch (status) {
    case "unresolved":
      return (
        <div className="status-tag">
          <span className="status-text chua-xu-ly"><IntlMessages id="shopee.workspace.issues.status.unresolved" /></span>
        </div>
      );
    case "in_progress":
      return (
        <div className="status-tag">
          <span className="status-text dang-xu-ly"><IntlMessages id="shopee.workspace.issues.status.in-progress" /></span>
          <span className="status-assignee">👤 {assignee || "Minh H."}</span>
        </div>
      );
    case "pending":
      return (
        <div className="status-tag">
          <span className="status-text cho-xu-ly"><IntlMessages id="shopee.workspace.issues.status.waiting" /></span>
        </div>
      );
    case "resolved":
      return (
        <div className="status-tag">
          <span className="status-text da-khac-phuc"><IntlMessages id="shopee.workspace.issues.status.resolved" /></span>
          <span className="status-assignee">
            {time || "09:50"} | {assignee || "Huy P."}
          </span>
        </div>
      );
    case "ignored":
    default:
      return (
        <div className="status-tag">
          <span className="status-text bo-qua"><IntlMessages id="shopee.workspace.issues.status.ignored" /></span>
        </div>
      );
  }
};

// SVG Donut Chart Component
const DonutChart = ({ data, totalLabel = "1.248", centerText, centerTextId }) => {
  // calculate SVG arcs
  let accumulatedAngle = 0;
  const radius = 45;
  const cx = 60;
  const cy = 60;

  const slices = data.map((item) => {
    const angle = (item.pct / 100) * 360;
    const startAngle = accumulatedAngle;
    const endAngle = accumulatedAngle + angle;
    accumulatedAngle += angle;

    const x1 = cx + radius * Math.cos((Math.PI * (startAngle - 90)) / 180);
    const y1 = cy + radius * Math.sin((Math.PI * (startAngle - 90)) / 180);
    const x2 = cx + radius * Math.cos((Math.PI * (endAngle - 90)) / 180);
    const y2 = cy + radius * Math.sin((Math.PI * (endAngle - 90)) / 180);

    const largeArcFlag = angle > 180 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    return { ...item, d };
  });

  return (
    <div className="donut-chart-container">
      <div className="donut-svg-wrap">
        <svg viewBox="0 0 120 120" width="100%" height="100%">
          {slices.map((slice, i) => (
            <path key={i} d={slice.d} fill={slice.color} />
          ))}
          {/* Inner white circle for donut hole */}
          <circle cx="60" cy="60" r="32" fill="#ffffff" />
        </svg>
        <div className="donut-center-text">
          <div className="center-val">{totalLabel}</div>
          <div className="center-lbl">
            {centerTextId ? (
              <IntlMessages id={centerTextId} />
            ) : (
              centerText
            )}
          </div>
        </div>
      </div>
      <div className="donut-legend-list">
        {data.map((item, i) => (
          <div className="legend-item" key={i}>
            <span className="legend-label">
              <span className="dot" style={{ backgroundColor: item.color }} />
              {item.name}
            </span>
            <span className="legend-val">
              {item.count} ({item.pct}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// SVG Line Chart Component for Timeline
const SeverityLineChart = () => {
  const dates = ["16/07", "17/07", "18/07", "19/07", "20/07", "21/07", "22/07"];
  // Normalized points for 4 lines (y from 10 to 140)
  const lineNghiemTrong = "10,120 70,125 130,118 190,122 250,115 310,120 370,116";
  const lineCao = "10,90 70,85 130,95 190,88 250,92 310,84 370,90";
  const lineTrungBinh = "10,60 70,55 130,68 190,52 250,60 310,54 370,58";
  const lineThap = "10,35 70,30 130,22 190,28 250,20 310,25 370,22";

  return (
    <div className="w-100">
      <div className="d-flex align-items-center justify-content-end gap-3 mb-2" style={{ fontSize: "0.76rem" }}>
        <span className="d-inline-flex align-items-center me-2">
          <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: "#ef4444" }} />
          <IntlMessages id="shopee.workspace.issues.severity.critical" />
        </span>
        <span className="d-inline-flex align-items-center me-2">
          <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: "#f97316" }} />
          <IntlMessages id="shopee.workspace.issues.severity.high" />
        </span>
        <span className="d-inline-flex align-items-center me-2">
          <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: "#eab308" }} />
          <IntlMessages id="shopee.workspace.issues.severity.medium" />
        </span>
        <span className="d-inline-flex align-items-center">
          <span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, background: "#10b981" }} />
          <IntlMessages id="shopee.workspace.issues.severity.low" />
        </span>
      </div>
      <div className="line-chart-wrap">
        <svg viewBox="0 0 380 150" preserveAspectRatio="none">
          {/* Horizontal Grid lines */}
          <line x1="0" y1="25" x2="380" y2="25" stroke="#f1f5f9" strokeDasharray="3,3" />
          <line x1="0" y1="60" x2="380" y2="60" stroke="#f1f5f9" strokeDasharray="3,3" />
          <line x1="0" y1="95" x2="380" y2="95" stroke="#f1f5f9" strokeDasharray="3,3" />
          <line x1="0" y1="130" x2="380" y2="130" stroke="#f1f5f9" strokeDasharray="3,3" />

          {/* Polyline series */}
          <polyline fill="none" stroke="#ef4444" strokeWidth="2" points={lineNghiemTrong} />
          <polyline fill="none" stroke="#f97316" strokeWidth="2" points={lineCao} />
          <polyline fill="none" stroke="#eab308" strokeWidth="2" points={lineTrungBinh} />
          <polyline fill="none" stroke="#10b981" strokeWidth="2" points={lineThap} />

          {/* Data Points */}
          {lineNghiemTrong.split(" ").map((pt, i) => {
            const [x, y] = pt.split(",");
            return <circle key={i} cx={x} cy={y} r="3" fill="#ef4444" />;
          })}
          {lineThap.split(" ").map((pt, i) => {
            const [x, y] = pt.split(",");
            return <circle key={i} cx={x} cy={y} r="3" fill="#10b981" />;
          })}
        </svg>
      </div>
      <div className="d-flex justify-content-between px-1 text-muted" style={{ fontSize: "0.72rem", marginTop: 4 }}>
        {dates.map((d, idx) => (
          <span key={idx}>{d}</span>
        ))}
      </div>
    </div>
  );
};

export default injectIntl(function ShopeeIssueCenter({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const issues = workspace?.data?.issues || {};
  const orders = issues.orders || EMPTY_ROWS;
  const mappings = issues.mappings || EMPTY_ROWS;
  const jobs = issues.jobs || EMPTY_ROWS;

  // Filter States
  const [activeTab, setActiveTab] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [selectedTypeError, setSelectedTypeError] = useState("all");
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRows, setSelectedRows] = useState({});
  const [activeDrawerIssue, setActiveDrawerIssue] = useState(null);
  const [showPayloadReq, setShowPayloadReq] = useState(false);
  const [showPayloadRes, setShowPayloadRes] = useState(false);
  const [checklist, setChecklist] = useState({
    c1: true,
    c2: false,
    c3: false,
    c4: false
  });
  const [internalNote, setInternalNote] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  // Transform real workspace issues into UI format
  const issueList = useMemo(() => {
    const list = [];
    (orders || []).forEach((o, idx) => {
      list.push({
        id: `ORD-${o.id || o.order_sn || idx}`,
        time: o.updatedAt ? new Date(o.updatedAt).toLocaleString("vi-VN") : intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        timeAgo: intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        channel: "Shopee",
        typeCode: "orders",
        type: intl.formatMessage({ id: "shopee.workspace.issues.type.order-sync" }),
        severity: "high",
        object: `${intl.formatMessage({ id: "shopee.workspace.common.order-hash" })} #${o.order_sn || o.id}`,
        errorTitle: o.sync_error || o.conflict_state || intl.formatMessage({ id: "shopee.workspace.issues.error.order-sync" }),
        errorDetail: o.conflict_message || o.last_sync_error || intl.formatMessage({ id: "shopee.workspace.issues.error.check-order-status" }),
        status: o.resolved ? "resolved" : "unresolved",
        errorCode: "SHOPEE_ORDER_SYNC_ERR",
        endpoint: "/api/shopee/v2/orders",
        affectedOrders: 1,
        affectedProducts: "-",
        affectedRevenue: o.total_amount ? `${Number(o.total_amount).toLocaleString("vi-VN")} đ` : "0 đ"
      });
    });
    (mappings || []).forEach((m, idx) => {
      list.push({
        id: `MAP-${m.id || idx}`,
        time: m.updatedAt ? new Date(m.updatedAt).toLocaleString("vi-VN") : intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        timeAgo: intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        channel: "Shopee",
        typeCode: "products",
        type: intl.formatMessage({ id: "shopee.workspace.issues.type.product-sync" }),
        severity: "medium",
        object: `${intl.formatMessage({ id: "shopee.workspace.common.product-hash" })} #${m.platform_sku || m.platform_item_id}`,
        errorTitle: m.last_sync_error || m.conflict_state || intl.formatMessage({ id: "shopee.workspace.issues.error.sku-mapping" }),
        errorDetail: m.conflict_message || intl.formatMessage({ id: "shopee.workspace.issues.error.sku-unmapped" }),
        status: m.product_id ? "resolved" : "unresolved",
        errorCode: "SHOPEE_MAPPING_ERR",
        endpoint: "/api/shopee/v2/mappings",
        affectedOrders: "-",
        affectedProducts: 1,
        affectedRevenue: m.platform_price ? `${Number(m.platform_price).toLocaleString("vi-VN")} đ` : "0 đ"
      });
    });
    (jobs || []).forEach((j, idx) => {
      list.push({
        id: `JOB-${j.id || idx}`,
        time: j.createdAt ? new Date(j.createdAt).toLocaleString("vi-VN") : intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        timeAgo: intl.formatMessage({ id: "shopee.workspace.overview.activity.just-now" }),
        channel: "Shopee",
        typeCode: /catalog|product|mapping/i.test(String(j.resource || ""))
          ? "products"
          : /order|return/i.test(String(j.resource || ""))
            ? "orders"
            : "system",
        type: j.action || intl.formatMessage({ id: "shopee.workspace.issues.type.system-task" }),
        severity: j.status === "dead" ? "critical" : "high",
        object: `Job #${j.id} (${j.resource || "shopee"})`,
        errorTitle:
          j.last_error ||
          intl.formatMessage(
            { id: "shopee.workspace.issues.error.job-ended-status" },
            { status: j.status }
          ),
        errorDetail: intl.formatMessage(
          { id: "shopee.workspace.issues.error.retried-times" },
          { count: j.attempts || 1 }
        ),
        status: j.status === "completed" ? "resolved" : "unresolved",
        errorCode: "SHOPEE_JOB_FAIL",
        endpoint: "/api/shopee/v2/jobs",
        affectedOrders: "-",
        affectedProducts: "-",
        affectedRevenue: "0 đ"
      });
    });

    return list;
  }, [orders, mappings, jobs, intl]);

  // Filter issues based on active Tab and Dropdown controls
  const filteredIssues = useMemo(() => {
    return issueList.filter((item) => {
      // Tab filter
      if (activeTab === "unresolved" && item.status !== "unresolved") return false;
      if (activeTab === "in_progress" && item.status !== "in_progress") return false;
      if (activeTab === "resolved" && item.status !== "resolved") return false;
      if (activeTab === "ignored" && item.status !== "ignored") return false;

      // Channel dropdown
      if (selectedChannel !== "all" && item.channel.toLowerCase() !== selectedChannel.toLowerCase()) {
        return false;
      }
      // Type error dropdown
      if (selectedTypeError !== "all" && item.typeCode !== selectedTypeError) {
        return false;
      }
      // Severity dropdown
      if (selectedSeverity !== "all" && item.severity !== selectedSeverity) {
        return false;
      }
      // Status dropdown
      if (selectedStatus !== "all" && item.status !== selectedStatus) {
        return false;
      }

      return true;
    });
  }, [issueList, activeTab, selectedChannel, selectedTypeError, selectedSeverity, selectedStatus]);

  // Checkbox toggle helpers
  const isAllSelected = useMemo(() => {
    return (
      filteredIssues.length > 0 &&
      filteredIssues.every((row) => selectedRows[row.id])
    );
  }, [filteredIssues, selectedRows]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows({});
    } else {
      const next = {};
      filteredIssues.forEach((row) => {
        next[row.id] = true;
      });
      setSelectedRows(next);
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Open drawer detail
  const openDetailDrawer = (issue) => {
    setActiveDrawerIssue(issue);
    setShowPayloadReq(false);
    setShowPayloadRes(false);
  };

  // ReactTable Column Definitions
  const columns = useMemo(
    () => [
      {
        Header: (
          <Input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            style={{ margin: 0, cursor: "pointer" }}
          />
        ),
        id: "selection",
        accessor: (d) => d.id,
        Cell: (row) => (
          <Input
            type="checkbox"
            checked={Boolean(selectedRows[row.value])}
            onChange={(e) => {
              e.stopPropagation();
              toggleSelectRow(row.value);
            }}
            style={{ margin: 0, cursor: "pointer" }}
          />
        ),
        width: 44,
        sortable: false,
        resizable: false
      },
      {
        Header: <IntlMessages id="shopee.workspace.orders.col.time" />,
        accessor: "time",
        Cell: (row) => (
          <div>
            <div className="font-weight-bold" style={{ fontSize: "0.83rem" }}>
              {row.original.time}
            </div>
            <small className="text-muted">{row.original.timeAgo}</small>
          </div>
        ),
        width: 150
      },
      {
        Header: <IntlMessages id="shopee.workspace.issues.col.channel-source" />,
        accessor: "channel",
        Cell: (row) => <ChannelBadge channel={row.value} />,
        width: 140
      },
      {
        Header: <IntlMessages id="shopee.workspace.issues.col.type" />,
        accessor: "type",
        Cell: (row) => <span className="font-weight-600">{row.value}</span>,
        width: 140
      },
      {
        Header: <IntlMessages id="shopee.workspace.issues.col.severity" />,
        accessor: "severity",
        Cell: (row) => <SeverityBadge severity={row.value} />,
        width: 120
      },
      {
        Header: <IntlMessages id="shopee.workspace.issues.col.object" />,
        accessor: "object",
        Cell: (row) => (
          <span style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#1e293b" }}>
            {row.value}
          </span>
        ),
        width: 160
      },
      {
        Header: <IntlMessages id="shopee.workspace.issues.col.message" />,
        accessor: "errorTitle",
        Cell: (row) => (
          <div style={{ lineHeight: 1.35 }}>
            <div className="font-weight-bold" style={{ color: "#0f172a" }}>
              {row.original.errorTitle}
            </div>
            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
              {row.original.errorDetail}
            </div>
          </div>
        )
      },
      {
        Header: <IntlMessages id="shopee.workspace.returns.col.status" />,
        accessor: "status",
        Cell: (row) => (
          <StatusBadge
            status={row.value}
            assignee={row.original.assignee}
            time={row.original.resolvedTime}
          />
        ),
        width: 140
      },
      {
        Header: <IntlMessages id="shopee.workspace.actions" />,
        id: "actions",
        accessor: (d) => d,
        Cell: (row) => (
          <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
            <DropdownToggle color="light" size="sm" className="px-2 py-1 border-0">
              •••
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => openDetailDrawer(row.value)}>
                🔍 <IntlMessages id="shopee.workspace.returns.view-detail-title" />
              </DropdownItem>
              <DropdownItem
                onClick={() =>
                  setConfirmation({
                    action: "retry",
                    label: intl.formatMessage(
                  { id: "shopee.workspace.issues.action.process-label" },
                  { id: row.value.id }
                ),
                    ids: [row.value.id]
                  })
                }
              >
                ⚡ <IntlMessages id="shopee.workspace.issues.action.process-now" />
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                onClick={() =>
                  setConfirmation({
                    action: "ignore",
                    label: intl.formatMessage(
                  { id: "shopee.workspace.issues.action.ignore-label" },
                  { id: row.value.id }
                ),
                    ids: [row.value.id]
                  })
                }
              >
                🚫 <IntlMessages id="shopee.workspace.issues.action.ignore" />
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
        width: 80,
        sortable: false,
        style: { textAlign: "center" }
      }
    ],
    [isAllSelected, selectedRows]
  );
  // Compute chart data & KPI cards dynamically from issueList
  const issueKpis = useMemo(() => {
    const total = issueList.length;
    const unresolved = issueList.filter((i) => i.status === "unresolved").length;
    const inProgress = issueList.filter((i) => i.status === "in_progress").length;
    const resolved = issueList.filter((i) => i.status === "resolved").length;
    const ignored = issueList.filter((i) => i.status === "ignored").length;
    const critical = issueList.filter((i) => i.severity === "critical" || i.severity === "high").length;

    // Severity breakdown
    const countCritical = issueList.filter((i) => i.severity === "critical").length;
    const countCao = issueList.filter((i) => i.severity === "Cao").length;
    const countTB = issueList.filter((i) => i.severity === "medium").length;
    const countThap = issueList.filter((i) => i.severity === "low").length;

    const severityChartData = [
      { nameId: "shopee.workspace.issues.severity.critical", count: countCritical, pct: total ? Math.round((countCritical / total) * 100) : 0, color: "#ef4444" },
      { name: "Cao", count: countCao, pct: total ? Math.round((countCao / total) * 100) : 0, color: "#f97316" },
      { nameId: "shopee.workspace.issues.severity.medium", count: countTB, pct: total ? Math.round((countTB / total) * 100) : 0, color: "#eab308" },
      { nameId: "shopee.workspace.issues.severity.low", count: countThap, pct: total ? Math.round((countThap / total) * 100) : 0, color: "#10b981" }
    ];

    // Channel chart data
    const channelCounts = {};
    issueList.forEach((i) => {
      channelCounts[i.channel] = (channelCounts[i.channel] || 0) + 1;
    });
    const channelColors = { Shopee: "#ee4d2d", "TikTok Shop": "#000000", Lazada: "#0f146d", Website: "#0284c7", GHN: "#f97316" };
    const channelChartData = Object.entries(channelCounts).map(([name, count]) => ({
      name,
      count,
      pct: total ? Math.round((count / total) * 100) : 0,
      color: channelColors[name] || "#94a3b8"
    }));
    if (channelChartData.length === 0) {
      channelChartData.push({ name: "Shopee", count: 0, pct: 0, color: "#ee4d2d" });
    }

    // Type chart data
    const typeCounts = {};
    issueList.forEach((i) => {
      typeCounts[i.type] = (typeCounts[i.type] || 0) + 1;
    });
    const typeColors = ["#1265ee", "#8b5cf6", "#06b6d4", "#ef4444", "#f97316", "#94a3b8"];
    const typeChartData = Object.entries(typeCounts).map(([name, count], idx) => ({
      name,
      count,
      pct: total ? Math.round((count / total) * 100) : 0,
      color: typeColors[idx % typeColors.length]
    }));
    if (typeChartData.length === 0) {
      typeChartData.push({ nameId: "shopee.workspace.issues.type.system", count: 0, pct: 0, color: "#1265ee" });
    }

    return { total, unresolved, inProgress, resolved, ignored, critical, severityChartData, channelChartData, typeChartData };
  }, [issueList]);

  return (
    <div className="shopee-issue-center">
      {/* Header bar (no sidebar / top bar above KPI as requested) */}
      <div className="sic-header">
        <div className="sic-header-title">
<h4><IntlMessages id="shopee.workspace.tab.issues" /></h4>
<p><IntlMessages id="shopee.workspace.issues.subtitle" /></p>
        </div>
        <div className="sic-header-actions">
          <Button color="light" size="sm" className="font-weight-600 border me-2">
            ⚙️ <IntlMessages id="shopee.workspace.issues.action.configure-alerts" />
          </Button>
          <Button color="light" size="sm" className="font-weight-600 border me-2">
            📋 <IntlMessages id="shopee.workspace.issues.action.system-logs" />
          </Button>
          <Button
            color="primary"
            size="sm"
            className="font-weight-600 me-2"
            onClick={() => workspace?.refresh && workspace.refresh()}
          >
            🔄 <IntlMessages id="shopee.workspace.sidebar.refresh-title" />
          </Button>
          <Button color="light" size="sm" className="border px-2">
            •••
          </Button>
        </div>
      </div>

      {/* 6 KPI Cards Grid */}
      <div className="sic-kpis-grid">
        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-blue">📊</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.issues.kpi.all" /></div>
            <div className="sic-kpi-value">{issueKpis.total}</div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.all-sub" /></div>
          </div>
        </div>

        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-orange">⚠️</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.issues.status.unresolved" /></div>
            <div className="sic-kpi-value">{issueKpis.unresolved}</div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.unresolved-sub" /></div>
          </div>
        </div>

        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-purple">⚙️</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.issues.kpi.in-progress" /></div>
            <div className="sic-kpi-value">{issueKpis.inProgress}</div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.in-progress-sub" /></div>
          </div>
        </div>

        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-green">✅</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.issues.kpi.resolved" /></div>
            <div className="sic-kpi-value">{issueKpis.resolved}</div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.resolved-sub" /></div>
          </div>
        </div>

        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-red">🚨</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.issues.kpi.critical" /></div>
            <div className="sic-kpi-value">{issueKpis.critical}</div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.critical-sub" /></div>
          </div>
        </div>

        <div className="sic-kpi-card">
          <div className="sic-kpi-icon is-cyan">🎯</div>
          <div className="sic-kpi-info">
            <div className="sic-kpi-label"><IntlMessages id="shopee.workspace.finance.widget.success-rate" /></div>
            <div className="sic-kpi-value">
              {issueKpis.total > 0 ? `${Math.round((issueKpis.resolved / issueKpis.total) * 100)}%` : "100%"}
            </div>
            <div className="sic-kpi-sub font-weight-normal text-muted"><IntlMessages id="shopee.workspace.issues.kpi.resolved-sub" /></div>
          </div>
        </div>
      </div>

      {/* Main Table Container Card with Tabs & Filters */}
      <div className="sic-main-card">
        {/* Navigation Tabs */}
        <div className="sic-tab-nav">
          <div
            className={`sic-tab-item ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            <span><IntlMessages id="shopee.workspace.catalog.channel-all" /></span>
            <span className={`sic-badge-count ${activeTab === "all" ? "is-active" : ""}`}>
              {issueKpis.total}
            </span>
          </div>

          <div
            className={`sic-tab-item ${activeTab === "unresolved" ? "active" : ""}`}
            onClick={() => setActiveTab("unresolved")}
          >
            <span><IntlMessages id="shopee.workspace.issues.status.unresolved" /></span>
            <span className="sic-badge-count is-red">{issueKpis.unresolved}</span>
          </div>

          <div
            className={`sic-tab-item ${activeTab === "in_progress" ? "active" : ""}`}
            onClick={() => setActiveTab("in_progress")}
          >
            <span><IntlMessages id="shopee.workspace.issues.status.in-progress" /></span>
            <span className="sic-badge-count is-active">{issueKpis.inProgress}</span>
          </div>

          <div
            className={`sic-tab-item ${activeTab === "resolved" ? "active" : ""}`}
            onClick={() => setActiveTab("resolved")}
          >
            <span><IntlMessages id="shopee.workspace.issues.kpi.resolved" /></span>
            <span className="sic-badge-count is-green">{issueKpis.resolved}</span>
          </div>

          <div
            className={`sic-tab-item ${activeTab === "ignored" ? "active" : ""}`}
            onClick={() => setActiveTab("ignored")}
          >
            <span><IntlMessages id="shopee.workspace.issues.status.ignored" /></span>
            <span className="sic-badge-count">{issueKpis.ignored}</span>
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div className="sic-filter-bar">
          <Input
            type="select"
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            style={{ width: 140 }}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-channels" })}</option>
            <option value="shopee">Shopee</option>
            <option value="tiktok shop">TikTok Shop</option>
            <option value="lazada">Lazada</option>
            <option value="website">Website</option>
            <option value="ghn">GHN</option>
          </Input>

          <Input
            type="select"
            value={selectedTypeError}
            onChange={(e) => setSelectedTypeError(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.issues.filter.all-types" })}</option>
            <option value="orders">{intl.formatMessage({ id: "shopee.workspace.issues.type.order-sync" })}</option>
            <option value="products">{intl.formatMessage({ id: "shopee.workspace.issues.type.product-sync" })}</option>
            <option value="stock">{intl.formatMessage({ id: "shopee.workspace.issues.type.stock-sync" })}</option>
            <option value="api">{intl.formatMessage({ id: "shopee.workspace.issues.type.api-connection" })}</option>
            <option value="shipping">{intl.formatMessage({ id: "shopee.workspace.tab.shipping-short" })}</option>
            <option value="payment">{intl.formatMessage({ id: "shopee.workspace.issues.type.payment" })}</option>
          </Input>

          <Input
            type="select"
            value={selectedSeverity}
            onChange={(e) => setSelectedSeverity(e.target.value)}
            style={{ width: 140 }}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.issues.filter.all-severities" })}</option>
            <option value="critical">{intl.formatMessage({ id: "shopee.workspace.issues.severity.critical" })}</option>
            <option value="high">{intl.formatMessage({ id: "shopee.workspace.issues.severity.high" })}</option>
            <option value="medium">{intl.formatMessage({ id: "shopee.workspace.issues.severity.medium" })}</option>
            <option value="low">{intl.formatMessage({ id: "shopee.workspace.issues.severity.low" })}</option>
          </Input>

          <Input
            type="select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{ width: 150 }}
          >
            <option value="all">{intl.formatMessage({ id: "shopee.workspace.common.all-statuses" })}</option>
            <option value="unresolved">{intl.formatMessage({ id: "shopee.workspace.issues.status.unresolved" })}</option>
            <option value="in_progress">{intl.formatMessage({ id: "shopee.workspace.issues.status.in-progress" })}</option>
            <option value="waiting">{intl.formatMessage({ id: "shopee.workspace.issues.status.waiting" })}</option>
            <option value="resolved">{intl.formatMessage({ id: "shopee.workspace.issues.status.resolved" })}</option>
            <option value="ignored">{intl.formatMessage({ id: "shopee.workspace.issues.status.ignored" })}</option>
          </Input>

          <Input
            type="text"
            defaultValue="01/07/2024 - 22/07/2024"
            style={{ width: 180 }}
          />

          <Button color="light" size="sm" className="font-weight-600 border">
            🔍 <IntlMessages id="shopee.workspace.common.filter" />
          </Button>
        </div>

        {/* ReactTable Component */}
        <div className="sic-table-wrapper">
          <ReactTable
            data={filteredIssues}
            columns={columns}
            defaultPageSize={8}
            showPagination={false}
            className="-striped -highlight"
            noDataText={intl.formatMessage({ id: "shopee.workspace.issues.empty" })}
            getTrProps={(state, rowInfo) => {
              return {
                onClick: () => {
                  if (rowInfo?.original) {
                    openDetailDrawer(rowInfo.original);
                  }
                },
                style: { cursor: "pointer" }
              };
            }}
          />
        </div>

        {/* Pagination Footer */}
        <div className="sic-pagination">
          <div>Hiển thị 1 - {filteredIssues.length} <IntlMessages id="shopee.workspace.issues.pagination-of" values={{ total: issueKpis.total }} /></div>
          <div className="sic-page-buttons">
            <button className="btn-page" disabled>
              «
            </button>
            <button className="btn-page" disabled>
              ‹
            </button>
            <button className="btn-page active">1</button>
            <button className="btn-page">2</button>
            <button className="btn-page">3</button>
            <button className="btn-page">4</button>
            <button className="btn-page">5</button>
            <button className="btn-page">...</button>
            <button className="btn-page">156</button>
            <button className="btn-page">›</button>
            <button className="btn-page">»</button>
          </div>
          <div className="d-flex align-items-center gap-1">
            <Input type="select" size="sm" style={{ width: 110, fontSize: "0.8rem" }}>
              <option value="20">20 / trang</option>
              <option value="50">50 / trang</option>
              <option value="100">100 / trang</option>
            </Input>
          </div>
        </div>
      </div>

      {/* Bottom Analytics Section */}
      {/* Row 1: 3 Donut Charts */}
      <div className="sic-analytics-row-1">
        <div className="sic-analytics-card">
          <div className="card-title-heading"><IntlMessages id="shopee.workspace.issues.chart.by-channel" /></div>
          <DonutChart data={issueKpis.channelChartData} totalLabel={String(issueKpis.total)} centerTextId="shopee.workspace.issues.total-errors" />
        </div>

        <div className="sic-analytics-card">
<div className="card-title-heading"><IntlMessages id="shopee.workspace.issues.chart.by-type" /></div>
            <DonutChart data={issueKpis.typeChartData} totalLabel={String(issueKpis.total)} centerTextId="shopee.workspace.issues.total-errors" />
        </div>

        <div className="sic-analytics-card">
<div className="card-title-heading"><IntlMessages id="shopee.workspace.issues.chart.by-severity" /></div>
            <DonutChart data={issueKpis.severityChartData} totalLabel={String(issueKpis.total)} centerTextId="shopee.workspace.issues.total-errors" />
        </div>
      </div>

      {/* Row 2: Severity Trend Line Chart + Top Errors + Recent Activity */}
      <div className="sic-analytics-row-2">
        <div className="sic-analytics-card">
          <div className="card-title-heading"><IntlMessages id="shopee.workspace.issues.chart.timeline" /></div>
          <SeverityLineChart />
        </div>

        <div className="sic-analytics-card">
          <div className="card-title-heading"><IntlMessages id="shopee.workspace.issues.chart.top-errors" /></div>
          <div className="top-errors-list">
            <div className="error-rank-item">
              <span className="rank-num">1</span>
<span className="error-name"><IntlMessages id="shopee.workspace.issues.top.order-list" /></span>
<span className="error-count"><IntlMessages id="shopee.workspace.issues.times-count" values={{ count: 256 }} /></span>
            </div>
            <div className="error-rank-item">
              <span className="rank-num">2</span>
<span className="error-name"><IntlMessages id="shopee.workspace.issues.top.invalid-data" /></span>
<span className="error-count"><IntlMessages id="shopee.workspace.issues.times-count" values={{ count: 198 }} /></span>
            </div>
            <div className="error-rank-item">
              <span className="rank-num">3</span>
              <span className="error-name">Connection timeout</span>
              <span className="error-count"><IntlMessages id="shopee.workspace.issues.times-count" values={{ count: 156 }} /></span>
            </div>
            <div className="error-rank-item">
              <span className="rank-num">4</span>
<span className="error-name"><IntlMessages id="shopee.workspace.issues.top.negative-stock" /></span>
<span className="error-count"><IntlMessages id="shopee.workspace.issues.times-count" values={{ count: 98 }} /></span>
            </div>
            <div className="error-rank-item">
              <span className="rank-num">5</span>
<span className="error-name"><IntlMessages id="shopee.workspace.issues.top.payment-not-found" /></span>
<span className="error-count"><IntlMessages id="shopee.workspace.issues.times-count" values={{ count: 84 }} /></span>
            </div>
          </div>
        </div>

        <div className="sic-analytics-card">
          <div className="card-title-heading"><IntlMessages id="shopee.workspace.overview.activity-title" /></div>
          <div className="activity-timeline">
            <div className="activity-item">
              <span className="act-time">10:28</span>
              <span className="act-icon process">✓</span>
              <div className="act-content">
                <strong>Minh H.</strong> <IntlMessages id="shopee.workspace.issues.activity.processed" /> <span className="sku">SKU-99218</span>
              </div>
            </div>

            <div className="activity-item">
              <span className="act-time">09:50</span>
              <span className="act-icon check">✓</span>
              <div className="act-content">
                <strong>Huy P.</strong> <IntlMessages id="shopee.workspace.issues.activity.resolved" /> <span className="sku">SP240722-0105</span>
              </div>
            </div>

            <div className="activity-item">
              <span className="act-time">09:31</span>
              <span className="act-icon check">✓</span>
              <div className="act-content">
                <strong>Huy P.</strong> <IntlMessages id="shopee.workspace.issues.activity.resolved" /> <span className="sku">TT240722-1133</span>
              </div>
            </div>

            <div className="text-right mt-2">
              <a href="#activities" className="text-primary font-weight-600" style={{ fontSize: "0.8rem" }}>
                <IntlMessages id="shopee.workspace.common.view-all" /> →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Right Slide-over Detail Inspection Drawer ("Chi tiết lỗi") */}
      {activeDrawerIssue && (
        <div className="sic-drawer-backdrop" onClick={() => setActiveDrawerIssue(null)}>
          <div className="sic-drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-title" onClick={() => setActiveDrawerIssue(null)}>
                ‹ <IntlMessages id="shopee.workspace.issues.drawer.title" />
              </div>
              <button
                className="btn-close-drawer"
                onClick={() => setActiveDrawerIssue(null)}
              >
                ✕
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-issue-code">
                <h3>{activeDrawerIssue.id}</h3>
                <SeverityBadge severity={activeDrawerIssue.severity} />
              </div>

              <div className="drawer-status-row">
                <div>
                  <IntlMessages id="shopee.workspace.finance.meta.status" />{" "}
                  <strong className="ms-1">{activeDrawerIssue.status}</strong>
                </div>
                <ChannelBadge channel={activeDrawerIssue.channel} />
              </div>

              {/* Thông tin lỗi */}
              <div className="drawer-section">
                <div className="section-title"><IntlMessages id="shopee.workspace.issues.drawer.info-title" /></div>
                <div className="info-grid">
                  <div className="info-label"><IntlMessages id="shopee.workspace.orders.col.time" /></div>
                  <div className="info-value">
                    {activeDrawerIssue.time} ({activeDrawerIssue.timeAgo})
                  </div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.col.channel-source" /></div>
                  <div className="info-value">{activeDrawerIssue.channel}</div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.col.type" /></div>
                  <div className="info-value">{activeDrawerIssue.type}</div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.col.object" /></div>
                  <div className="info-value font-weight-bold">
                    {activeDrawerIssue.object}
                  </div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.col.message" /></div>
                  <div className="info-value error-box">
                    <strong>{activeDrawerIssue.errorTitle}</strong>
                    <div>{activeDrawerIssue.errorDetail}</div>
                  </div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.drawer.error-code" /></div>
                  <div className="info-value font-weight-600" style={{ color: "#ef4444" }}>
                    {activeDrawerIssue.errorCode || "SHOPEE_API_500"}
                  </div>

                  <div className="info-label"><IntlMessages id="shopee.workspace.issues.drawer.request" /></div>
                  <div className="info-value text-primary font-weight-600">
                    {activeDrawerIssue.endpoint || "GET /api/v2/order/get_order_list"}
                  </div>
                </div>

                <div
                  className="payload-toggle-btn"
                  onClick={() => setShowPayloadReq(!showPayloadReq)}
                >
                  <span><IntlMessages id="shopee.workspace.issues.drawer.request-detail" /></span>
                  <span>{showPayloadReq ? "▲" : "▼"}</span>
                </div>
                {showPayloadReq && (
                  <div className="payload-content">
                    {`{
  "shop_id": 1029384,
  "order_sn": "${activeDrawerIssue.id}",
  "timestamp": 1721619261
}`}
                  </div>
                )}

                <div
                  className="payload-toggle-btn"
                  onClick={() => setShowPayloadRes(!showPayloadRes)}
                >
                  <span><IntlMessages id="shopee.workspace.issues.drawer.response-detail" /></span>
                  <span>{showPayloadRes ? "▲" : "▼"}</span>
                </div>
                {showPayloadRes && (
                  <div className="payload-content">
                    {`{
  "error": "error_server",
  "message": "${activeDrawerIssue.errorDetail}",
  "request_id": "req_84719230918"
}`}
                  </div>
                )}
              </div>

              {/* Ảnh hưởng */}
              <div className="drawer-section">
                <div className="section-title"><IntlMessages id="shopee.workspace.issues.drawer.impact-title" /></div>
                <div className="impact-grid">
                  <div className="impact-item">
                    <div className="impact-val">{activeDrawerIssue.affectedOrders}</div>
                    <div className="impact-lbl"><IntlMessages id="shopee.workspace.issues.drawer.impact-orders" /></div>
                  </div>
                  <div className="impact-item">
                    <div className="impact-val">{activeDrawerIssue.affectedProducts}</div>
                    <div className="impact-lbl"><IntlMessages id="shopee.workspace.issues.drawer.impact-products" /></div>
                  </div>
                  <div className="impact-item">
                    <div className="impact-val">{activeDrawerIssue.affectedRevenue}</div>
                    <div className="impact-lbl"><IntlMessages id="shopee.workspace.issues.drawer.impact-revenue" /></div>
                  </div>
                </div>
              </div>

              {/* Gợi ý xử lý */}
              <div className="drawer-section">
                <div className="section-title"><IntlMessages id="shopee.workspace.issues.drawer.suggestions-title" /></div>
                <div className="suggestion-list">
                  <label className={`suggestion-item ${checklist.c1 ? "done" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checklist.c1}
                      onChange={() => setChecklist((prev) => ({ ...prev, c1: !prev.c1 }))}
                    />
                    <IntlMessages id="shopee.workspace.issues.suggest.check-api" />
                  </label>
                  <label className={`suggestion-item ${checklist.c2 ? "done" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checklist.c2}
                      onChange={() => setChecklist((prev) => ({ ...prev, c2: !prev.c2 }))}
                    />
                    <IntlMessages id="shopee.workspace.issues.suggest.check-status" />
                  </label>
                  <label className={`suggestion-item ${checklist.c3 ? "done" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checklist.c3}
                      onChange={() => setChecklist((prev) => ({ ...prev, c3: !prev.c3 }))}
                    />
                    <IntlMessages id="shopee.workspace.issues.suggest.retry-sync" />
                  </label>
                  <label className={`suggestion-item ${checklist.c4 ? "done" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checklist.c4}
                      onChange={() => setChecklist((prev) => ({ ...prev, c4: !prev.c4 }))}
                    />
                    <IntlMessages id="shopee.workspace.issues.suggest.contact-support" />
                  </label>
                </div>
              </div>

              {/* Ghi chú nội bộ */}
              <div className="drawer-section">
                <div className="section-title"><IntlMessages id="shopee.workspace.issues.drawer.note-title" /></div>
                <Input
                  type="textarea"
                  rows="3"
                  placeholder={intl.formatMessage({ id: "shopee.workspace.common.note-placeholder" })}
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  style={{ fontSize: "0.82rem", borderRadius: 8 }}
                />
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="drawer-footer">
              <Button
                color="secondary"
                outline
                size="sm"
                className="font-weight-600"
                onClick={() => {
                  setActiveDrawerIssue(null);
                }}
              >
                <IntlMessages id="shopee.workspace.issues.action.ignore" />
              </Button>
              <Button
                color="primary"
                outline
                size="sm"
                className="font-weight-600"
                onClick={() => {
                  setConfirmation({
                    action: "retry",
                    label: intl.formatMessage(
                  { id: "shopee.workspace.issues.action.process-label" },
                  { id: activeDrawerIssue.id }
                ),
                    ids: [activeDrawerIssue.id]
                  });
                }}
              >
                <IntlMessages id="shopee.workspace.issues.action.process-now" />
              </Button>
              <Button
                color="primary"
                size="sm"
                className="font-weight-600"
                onClick={() => {
                  setActiveDrawerIssue(null);
                }}
              >
                <IntlMessages id="shopee.workspace.issues.action.mark-resolved" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Remediation Action Modal */}
      <Modal
        isOpen={Boolean(confirmation)}
        toggle={() => setConfirmation(null)}
      >
        <ModalHeader toggle={() => setConfirmation(null)}>
          <IntlMessages id="shopee.workspace.issues.confirm.title" />
        </ModalHeader>
        <ModalBody>
          <IntlMessages id="shopee.workspace.issues.confirm.desc" />
          {confirmation ? (
            <div className="mt-2 font-weight-bold text-primary">
              <IntlMessages
                  id="shopee.workspace.issues.confirm.items"
                  values={{
                    label: confirmation.label,
                    count: confirmation.ids.length,
                  }}
                />
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmation(null)}>
            <IntlMessages id="shopee.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={async () => {
              if (!confirmation) return;
              const current = confirmation;
              setConfirmation(null);
              if (workspace?.runAction) {
                await workspace.runAction(
                  `issues-${current.action}`,
                  (key) =>
                    ShopeeV2Api.remediateIssues(
                      workspace.selectedShopId,
                      current.action,
                      current.ids,
                      key
                    ),
                  intl.formatMessage({ id: "shopee.workspace.issues.confirm.queued" })
                );
              }
            }}
          >
            <IntlMessages id="shopee.workspace.issues.confirm.ok" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
});
